import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getPageAccent } from "@/lib/pageAccents";

/**
 * Resolve the page accent color from the current pathname.
 * Mirrors the routing taxonomy used by the sidebar so accents match.
 */
function resolveAccent(pathname) {
  if (!pathname) return getPageAccent("default");
  // Match the leading segments first
  if (pathname.startsWith("/seccion/")) {
    const slug = pathname.split("/")[2]?.split("?")[0] || "default";
    return getPageAccent(slug);
  }
  if (pathname.startsWith("/admin/recompensas") || pathname.startsWith("/recompensas")) return getPageAccent("rewards");
  if (pathname.startsWith("/admin/evaluaciones") || pathname.startsWith("/evaluaciones")) return getPageAccent("evaluations");
  if (pathname.startsWith("/admin/idiomas") || pathname.startsWith("/idiomas")) return getPageAccent("languages");
  if (pathname.startsWith("/admin/financiamiento") || pathname.startsWith("/financiamiento")) return getPageAccent("financiamiento");
  if (pathname.startsWith("/admin/procesos") || pathname.startsWith("/procesos")) return getPageAccent("procesos");
  if (pathname.startsWith("/biblioteca")) return getPageAccent("library");
  if (pathname.startsWith("/mi-equipo") || pathname.startsWith("/comunidad")) return getPageAccent("team");
  if (pathname.startsWith("/admin")) return getPageAccent("admin");
  if (pathname.startsWith("/dashboard")) return getPageAccent("dashboard");
  return getPageAccent("default");
}

/**
 * Page transition wrapper.
 *
 * On every route change, draws a colored overlay matching the destination's
 * accent color and fades it out in ~350 ms. The brief flash reinforces the
 * "you just entered the orange/purple/green area" mental model without
 * blocking interaction.
 *
 * Honors prefers-reduced-motion: the overlay is skipped entirely for users
 * who opted out of motion.
 */
export default function PageTransition({ children }) {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) return;
    setVisible(true);
    const t = setTimeout(() => setVisible(false), 50);
    return () => clearTimeout(t);
  }, [pathname]);

  const accent = resolveAccent(pathname);

  return (
    <div className="relative" data-testid="page-transition-wrapper">
      {/* Accent flash overlay — pointer-events:none so it never blocks clicks */}
      <div
        aria-hidden
        className={`pointer-events-none fixed inset-0 z-[60] transition-opacity duration-500 ease-out ${
          visible ? "opacity-25" : "opacity-0"
        }`}
        style={{ backgroundColor: accent }}
        data-testid="page-transition-overlay"
      />
      {/* Content fade-in */}
      <div
        key={pathname}
        className="animate-[pageFadeIn_280ms_ease-out]"
        style={{ animationFillMode: "both" }}
      >
        {children}
      </div>
    </div>
  );
}
